import React from 'react';
import { Table, Tooltip } from 'antd';
import { Button } from '../../components/button';
import Papa from 'papaparse';
import { ButtonFileInput } from '../../components/button-file-input';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { SearchInput } from '../../components/search-input';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { ExportButton } from '../../components/action-buttons';
import { StoreHistoryBar } from '../../components/store-history-bar';
import { observer } from 'mobx-react';
import { ColumnProps } from 'antd/es/table';
import { Store } from '../../stores/stores';
import {
    StoreBrand,
    STORE_BRAND_LABELS,
    APPLICATION_TO_BRAND,
    BRAND_CSV_FILENAMES,
} from '../../stores/constants';
import {
    useAuthStore,
    useStoresStore,
    useApplicationSelectionStore,
} from '../../contexts/mobx';
import style from './style.module.scss';
import { useSearchParams } from '../../utils/use-search-params';
import { StoreTag } from '../../components/store-tag';
import {
    applyCoordOverrides,
    diffStores,
    DifferenceMap,
    findSuspectCoordChanges,
    ParsedStore,
    parseStoreFile,
    storeFilter,
    storesToJson,
} from '../../helpers/store-helper';
import { CoordPrecisionModal } from '../../components/coord-precision-modal';
import { useNavigation } from '../../utils/use-navigation';
import { getFileAsString, getFileEncoding } from '../../utils/csv';
import ElbowUpIcon from '../../icons/ArrowElbowRightUp.svg?react';
import TrashIcon from '../../icons/Trash.svg?react';
import XIcon from '../../icons/SmallX.svg?react';
import { Tag } from '../../components/tag';
import { StoreEditing } from '../../components/store-editing';
import { StoreEditingBatch } from '../../components/store-editing-batch';
import { StoreBatchConfirm } from '../../components/store-editing-batch-confirm';
import { PopupSuccess } from '../../components/popup-success';
import { saveAs } from 'file-saver';
import { PopupConfirmDelete } from '../../components/popup-confirm-delete';
import { TEN_PER_PAGE } from '../../utils/const';

type SearchParams = {
    perPage?: number;
    page?: number;
    q?: string;
};

export const StoresPage = observer(() => {
    const store = useStoresStore();
    const navigation = useNavigation();
    const [selectedStore, setSelectedStore] = React.useState<Store | null>(
        null
    );
    const [selectedStores, setSelectedStores] = React.useState<Store[]>([]);
    const [storeEditing, setStoreEditing] = React.useState(false);
    const [batchEditing, setBatchEditing] = React.useState(false);
    const [batchModifications, setBatchModifications] = React.useState<Store[]>(
        []
    );
    const [isBatchConfirm, setIsBatchConfirm] = React.useState(false);
    const search = useSearchParams<SearchParams>();
    const [searchValue, setSearchValue] = React.useState<string>();
    const [checkedList, setCheckedList] = React.useState<string[]>([]);
    const [checkAll, setCheckAll] = React.useState(false);
    const [isBatchPopupVisible, setIsBatchPopupVisible] = React.useState(false);
    const [isDeletePopupVisible, setIsDeletePopupVisible] = React.useState(
        false
    );
    const [
        isConfirmDeletePopupVisisble,
        setIsConfirmDeletePopupVisible,
    ] = React.useState(false);
    const [numberOfDeletedStores, setNumberOfDeletedStores] = React.useState(0);
    const [isLoadingCsv, setIsLoadingCsv] = React.useState(false);
    const [pendingParsed, setPendingParsed] = React.useState<
        ParsedStore[] | null
    >(null);
    const [coordSuspects, setCoordSuspects] = React.useState<
        Map<string, { lat?: number; lng?: number }>
    >(new Map());

    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();

    const brand: StoreBrand | undefined =
        APPLICATION_TO_BRAND[applicationSelectionStore.selectedApplication];

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const closeStoreEditing = () => {
        setStoreEditing((current) => !current);
    };

    const showStoreEditing = (store: Store) => {
        setSelectedStore(store);
        !storeEditing && setStoreEditing((current) => !current);
    };

    const closeBatchConfirm = () => {
        setBatchModifications([]);
        setIsBatchConfirm(false);
    };

    const closeBatchEditing = React.useCallback(() => {
        closeBatchConfirm();
        setBatchEditing(false);
    }, []);

    const showBatchEditing = () => {
        setBatchEditing(true);
    };

    const saveBatchModifications = (modifications: Store[]) => {
        setBatchModifications(modifications);
        setIsBatchConfirm(true);
    };

    const handleClear = () => {
        closeBatchEditing();
        setCheckedList([]);
        setCheckAll(false);
    };

    const waitAndFetchStores = () => {
        setTimeout(() => {
            store.fetchStores(brand);
        }, 500);
    };

    const handlePopupClose = () => {
        handleClear();
        setIsBatchPopupVisible(false);
        setIsDeletePopupVisible(false);
        waitAndFetchStores();
    };

    const handleBatchEditConfirm = (worked: boolean) => {
        setIsBatchPopupVisible(worked);
    };

    const handleSaveEdit = () => {
        waitAndFetchStores();
    };

    const handleDeleteConfirm = () => {
        setIsConfirmDeletePopupVisible(true);
    };

    const handleDelete = async () => {
        setIsConfirmDeletePopupVisible(false);
        const numberOfDeletedStores = await store.deleteStores({
            nrefs: checkedList,
        });
        setIsDeletePopupVisible(true);
        setNumberOfDeletedStores(numberOfDeletedStores);
    };

    const onCheckAllChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked;
            if (store.stores) {
                setCheckedList(
                    checked ? store.stores.map((store) => store.nref) : []
                );
                setSelectedStores(checked ? store.stores : []);
            }
            setCheckAll(checked);
            if (!checked) {
                closeBatchEditing();
            }
        },
        [closeBatchEditing, store.stores]
    );

    const onChangeCheckbox = React.useCallback(
        (nref: string, checked: boolean) => {
            const newCheckedList = checked
                ? [...checkedList, nref]
                : checkedList.filter((nrefValue) => nrefValue !== nref);

            setCheckedList(newCheckedList);
            const newSelectedStores =
                store.stores?.filter((store) =>
                    newCheckedList.includes(store.nref)
                ) ?? [];
            setSelectedStores(newSelectedStores);
            setCheckAll(
                store.stores
                    ? newCheckedList.length === store.stores?.length
                    : false
            );
            if (newCheckedList.length === 0) {
                closeBatchEditing();
            }
        },
        [checkedList, closeBatchEditing, store.stores]
    );

    const columns = React.useMemo<ColumnProps<Store>[]>(
        () => [
            {
                title: (
                    <label className={style.checkbox}>
                        <input
                            type="checkbox"
                            onChange={onCheckAllChange}
                            checked={checkAll}
                        />
                    </label>
                ),
                render: (_: unknown, record: Store) => (
                    <label className={style.checkbox}>
                        <input
                            type="checkbox"
                            checked={checkedList.includes(record.nref)}
                            onChange={(e) =>
                                onChangeCheckbox(record.nref, e.target.checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                        />
                    </label>
                ),
            },
            {
                title: 'Nref',
                dataIndex: 'nref',
                key: 'nref',
            },
            {
                title: 'Brand',
                dataIndex: 'brand',
                render: (_: unknown, record: Store) => {
                    if (record.brand === StoreBrand.COUCHE_TARD) {
                        return (
                            <Tag variant="brand-red">
                                {STORE_BRAND_LABELS[StoreBrand.COUCHE_TARD]}
                            </Tag>
                        );
                    }
                    if (record.brand === StoreBrand.CIRCLE_K) {
                        return (
                            <Tag variant="success">
                                {STORE_BRAND_LABELS[StoreBrand.CIRCLE_K]}
                            </Tag>
                        );
                    }
                    return null;
                },
            },
            {
                title: 'Address',
                dataIndex: 'address',
                render: (_: unknown, record: Store) =>
                    `${record.addr.street}, ${record.addr.city}, ${record.addr.post}`,
            },
            {
                title: 'Tel',
                dataIndex: 'tel',
                key: 'nref',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                render: (_: unknown, record: Store) => (
                    <StoreTag status={record.status} />
                ),
            },
        ],
        [checkAll, checkedList, onChangeCheckbox, onCheckAllChange]
    );

    React.useEffect(() => {
        store.fetchStores(brand);
    }, [brand, store]);

    const navigateToDiff = (parsed: ParsedStore[]) => {
        store.setParsedStore(parsed);
        navigation.goToStoresDiff();
    };

    const onFileSelected = async (file: File) => {
        setIsLoadingCsv(true);
        try {
            const encoding = await getFileEncoding(file);
            const text = await getFileAsString(file, encoding);

            const results = await new Promise<Papa.ParseResult<string[]>>(
                (resolve, reject) =>
                    Papa.parse<string[]>(text, {
                        complete: resolve,
                        error: reject,
                    })
            );

            const { stores: parsed, warnings } = parseStoreFile(results.data);
            store.setImportWarnings(
                warnings.missingColumns.length > 0 ||
                    warnings.unknownColumns.length > 0
                    ? warnings
                    : null
            );
            await store.fetchStores(brand);

            if (!store.stores) {
                NotificationManager.showError('Could not load stores');
                return;
            }
            const diff = diffStores(store.stores, parsed);
            const suspects = findSuspectCoordChanges(diff);

            if (suspects.size > 0) {
                store.setCoordImportState(parsed, suspects);
                setPendingParsed(parsed);
                setCoordSuspects(suspects);
            } else {
                store.clearCoordImportState();
                navigateToDiff(parsed);
            }
        } catch {
            NotificationManager.showError('Error parsing the file');
        } finally {
            setIsLoadingCsv(false);
        }
    };

    const handleCoordConfirm = (keepDbNrefs: Set<string>) => {
        if (!pendingParsed || !store.stores) return;
        const corrected = applyCoordOverrides(
            pendingParsed,
            store.stores,
            keepDbNrefs
        );
        const remainingSuspects = findSuspectCoordChanges(
            diffStores(store.stores, corrected)
        );
        store.setCoordImportState(pendingParsed, remainingSuspects);
        setPendingParsed(null);
        setCoordSuspects(new Map());
        navigateToDiff(corrected);
    };

    const handleCoordGoBack = () => {
        setPendingParsed(null);
        setCoordSuspects(new Map());
        store.clearCoordImportState();
    };

    const coordDifferences = React.useMemo(
        () =>
            pendingParsed && store.stores
                ? diffStores(store.stores, pendingParsed)
                : new DifferenceMap(),
        [store.stores, pendingParsed]
    );

    const exportStoresToCSV = (storesToExport: Store[]) => {
        const data = storesToJson(storesToExport);
        const csv = Papa.unparse(data);
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const filename = brand
            ? BRAND_CSV_FILENAMES[brand] ?? 'all_stores.csv'
            : 'all_stores.csv';
        saveAs(blob, filename);
    };

    const exportAllStoresToCSV = () => {
        if (store.stores && store.stores.length > 0) {
            exportStoresToCSV(store.stores);
        } else {
            NotificationManager.showError('No stores available to export');
        }
    };

    const exportSelectedStoresToCSV = () => {
        if (selectedStores.length > 0) {
            exportStoresToCSV(selectedStores);
        } else {
            NotificationManager.showError('No stores selected to export');
        }
    };

    const page = search.params.page ?? 0;
    const perPage = search.params.perPage ?? TEN_PER_PAGE;

    const stringifyQ =
        search.params.q !== undefined ? String(search.params.q) : undefined;
    const filteredStores = store.stores?.filter(storeFilter(stringifyQ));
    const start = page * perPage;

    const sortedStores = React.useMemo(() => {
        if (!filteredStores) {
            return [];
        }
        const selectedStores = filteredStores.filter((store) =>
            checkedList.includes(store.nref)
        );
        const unselectedStores = filteredStores.filter(
            (store) => !checkedList.includes(store.nref)
        );
        return [...selectedStores, ...unselectedStores];
    }, [filteredStores, checkedList]);

    const paginatedStores = sortedStores?.slice(start, start + perPage);

    const handleSearchClick = () =>
        search.setParams({ q: searchValue, page: 0 });
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        e.target.value !== ''
            ? setSearchValue(e.target.value)
            : (search.setParams({ q: e.target.value, page: 0 }),
              setSearchValue(e.target.value));
    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
        e.key === 'Enter' && search.setParams({ q: searchValue, page: 0 });

    return (
        <>
            <PageTopBar title="Stores">
                <Button
                    variant="tertiary"
                    className={style.not_allowed}
                    icon={<ElbowUpIcon className={style.icon} />}
                >
                    Revert Last Edit
                </Button>

                <ExportButton
                    onClick={() => exportAllStoresToCSV()}
                    loading={store.isFetchingStores}
                >
                    Export all stores
                </ExportButton>
                <Tooltip
                    title={
                        !brand
                            ? 'Sélectionnez une application pour importer'
                            : undefined
                    }
                >
                    <span
                        style={!brand ? { cursor: 'not-allowed' } : undefined}
                    >
                        <ButtonFileInput
                            text="Import CSV"
                            onFileSelected={onFileSelected}
                            disabled={store.isFetchingStores || !brand}
                            isLoading={isLoadingCsv}
                        />
                    </span>
                </Tooltip>
            </PageTopBar>

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={'query_box'}>
                        {/* TODO: Je remarque que les utilisations de <SearchInput /> sont souvent configurées différemment. Voulu? ou accidentel?*/}
                        <SearchInput
                            onClickIcon={handleSearchClick}
                            onChange={handleOnChange}
                            onKeyDown={handleOnKeyDown}
                        />
                    </div>
                </div>

                {checkedList.length > 0 && (
                    <div className={style.edit_buttons_container}>
                        <Button
                            variant="primary"
                            className={style.edit_store_button}
                            onClick={() => showBatchEditing()}
                        >
                            {checkedList.length > 1
                                ? `Edit ${checkedList.length} stores`
                                : 'Edit Store'}
                        </Button>
                        <Button
                            variant="tertiary"
                            icon={<ElbowUpIcon className={style.icon} />}
                            onClick={() => exportSelectedStoresToCSV()}
                        >
                            Export
                        </Button>
                        <Button
                            variant="tertiary-destructive"
                            icon={<TrashIcon className={style.icon} />}
                            onClick={() => handleDeleteConfirm()}
                        >
                            Delete
                        </Button>
                        <Button
                            variant="tertiary"
                            icon={<XIcon className={style.icon} />}
                            onClick={() => handleClear()}
                        >
                            Cancel
                        </Button>
                    </div>
                )}

                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: filteredStores?.length ?? 0,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<Store>
                className="clickable_table_body_rows"
                rowKey="id"
                columns={columns}
                scroll={{ x: 900 }}
                dataSource={paginatedStores ?? []}
                loading={store.isFetchingStores}
                pagination={false}
                onRow={(record) => ({
                    onClick: () => {
                        showStoreEditing(record);
                    },
                    className: checkedList.includes(record.nref)
                        ? style.selected_row
                        : '',
                })}
            />

            <StoreHistoryBar />

            <div
                className={
                    storeEditing
                        ? `${style.store_editing} ${style.active}`
                        : `${style.store_editing}`
                }
            >
                {selectedStore && (
                    <StoreEditing
                        onCloseClick={closeStoreEditing}
                        storeObject={selectedStore}
                        store={store}
                        onSave={handleSaveEdit}
                    />
                )}
            </div>
            <div
                className={
                    batchEditing
                        ? `${style.store_editing} ${style.active}`
                        : `${style.store_editing}`
                }
            >
                {batchModifications.length > 0 && isBatchConfirm ? (
                    <StoreBatchConfirm
                        onCloseClick={closeBatchEditing}
                        modifications={batchModifications}
                        store={store}
                        goBack={closeBatchConfirm}
                        onConfirm={handleBatchEditConfirm}
                    />
                ) : (
                    <StoreEditingBatch
                        onCloseClick={closeBatchEditing}
                        storeObjects={selectedStores}
                        saveBatchModifications={saveBatchModifications}
                    />
                )}
            </div>
            {isBatchPopupVisible && (
                <PopupSuccess
                    message={`Your modifications to the ${batchModifications.length} selected stores were successfully saved!`}
                    onClose={handlePopupClose}
                />
            )}
            {isConfirmDeletePopupVisisble && (
                <PopupConfirmDelete
                    message={
                        checkedList.length > 1
                            ? `Are you sure you want to delete ${checkedList.length} stores?`
                            : `Are you sure you want to delete the store?`
                    }
                    onClose={() => setIsConfirmDeletePopupVisible(false)}
                    onOk={handleDelete}
                />
            )}
            {isDeletePopupVisible && (
                <PopupSuccess
                    message={
                        numberOfDeletedStores > 1
                            ? `${numberOfDeletedStores} stores were successfully deleted!`
                            : `${numberOfDeletedStores} store was successfully deleted!`
                    }
                    onClose={handlePopupClose}
                />
            )}
            {pendingParsed && coordSuspects.size > 0 && (
                <CoordPrecisionModal
                    suspects={coordSuspects}
                    differences={coordDifferences}
                    onConfirm={handleCoordConfirm}
                    onGoBack={handleCoordGoBack}
                />
            )}
        </>
    );
});
